import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Shield, Search, Eye, Loader2 } from "lucide-react";
import BackButton from "@/components/BackButton";
import { useToast } from "@/hooks/use-toast";

interface AuditEntry {
  id: string;
  occurred_at: string;
  user_id: string | null;
  user_email: string | null;
  table_name: string;
  action: "INSERT" | "UPDATE" | "DELETE";
  record_id: string | null;
  company_id: string | null;
  old_data: any;
  new_data: any;
}

const TABLE_LABELS: Record<string, string> = {
  supplier_master: "Maestro proveedores",
  company_account_catalog: "Catálogo cuentas",
  leasing_contracts: "Contratos leasing",
  leasing_schedule: "Cuadro leasing",
  user_company_access: "Acceso empresas",
  user_roles: "Roles de usuario",
  company_masters: "Empresas",
  supplier_corrections: "Correcciones proveedor",
  supplier_account_history: "Historial cuentas",
};

const ACTION_VARIANTS: Record<string, "default" | "secondary" | "destructive"> = {
  INSERT: "default",
  UPDATE: "secondary",
  DELETE: "destructive",
};

const AuditLog = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTable, setFilterTable] = useState<string>("all");
  const [filterAction, setFilterAction] = useState<string>("all");
  const [filterUser, setFilterUser] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) navigate("/app");
  }, [authLoading, isAdmin, navigate]);

  const loadEntries = async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("audit_log")
      .select("*")
      .order("occurred_at", { ascending: false })
      .limit(500);

    if (error) {
      toast({ title: "Error cargando auditoría", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }
    setEntries((data ?? []) as AuditEntry[]);
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) loadEntries();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterTable !== "all" && e.table_name !== filterTable) return false;
      if (filterAction !== "all" && e.action !== filterAction) return false;
      if (filterUser && !(e.user_email || "").toLowerCase().includes(filterUser.toLowerCase())) return false;
      if (filterFrom && new Date(e.occurred_at) < new Date(filterFrom)) return false;
      if (filterTo && new Date(e.occurred_at) > new Date(filterTo + "T23:59:59")) return false;
      return true;
    });
  }, [entries, filterTable, filterAction, filterUser, filterFrom, filterTo]);

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BackButton to="/admin" variant="ghost" />
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold">Registro de auditoría</h1>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadEntries} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Actualizar"}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 bg-card rounded-lg border">
          <div>
            <Label className="text-xs">Tabla</Label>
            <Select value={filterTable} onValueChange={setFilterTable}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(TABLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Acción</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="INSERT">Crear</SelectItem>
                <SelectItem value="UPDATE">Modificar</SelectItem>
                <SelectItem value="DELETE">Eliminar</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Usuario</Label>
            <div className="relative">
              <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={filterUser} onChange={(e) => setFilterUser(e.target.value)} placeholder="email" className="pl-7" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </div>
        </div>

        <div className="bg-card rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Tabla</TableHead>
                <TableHead>Acción</TableHead>
                <TableHead>ID registro</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sin resultados</TableCell></TableRow>
              ) : filtered.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {new Date(e.occurred_at).toLocaleString("es-ES")}
                  </TableCell>
                  <TableCell className="text-xs">{e.user_email || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs">{TABLE_LABELS[e.table_name] || e.table_name}</TableCell>
                  <TableCell>
                    <Badge variant={ACTION_VARIANTS[e.action]} className="text-xs">
                      {e.action === "INSERT" ? "Crear" : e.action === "UPDATE" ? "Modificar" : "Eliminar"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {e.record_id ? e.record_id.slice(0, 8) : "—"}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => setSelected(e)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!loading && entries.length >= 500 && (
            <div className="p-3 text-xs text-muted-foreground text-center border-t">
              Mostrando los últimos 500 eventos. Refina los filtros para ver más antiguos.
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del evento</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Fecha:</span> {new Date(selected.occurred_at).toLocaleString("es-ES")}</div>
                <div><span className="text-muted-foreground">Usuario:</span> {selected.user_email || "—"}</div>
                <div><span className="text-muted-foreground">Tabla:</span> {TABLE_LABELS[selected.table_name] || selected.table_name}</div>
                <div><span className="text-muted-foreground">Acción:</span> {selected.action}</div>
                <div className="col-span-2"><span className="text-muted-foreground">ID:</span> <span className="font-mono text-xs">{selected.record_id || "—"}</span></div>
              </div>
              {selected.old_data && (
                <div>
                  <Label className="text-xs text-muted-foreground">Valor anterior</Label>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.old_data, null, 2)}</pre>
                </div>
              )}
              {selected.new_data && (
                <div>
                  <Label className="text-xs text-muted-foreground">Valor nuevo</Label>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(selected.new_data, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLog;
